import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DashboardCrmService} from '@service/dashboard-crm/dashboard-crm.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UntypedFormBuilder} from '@angular/forms';
import {TreeComponent} from '../../../components/v2/tree/tree.component';
import {Subject} from 'rxjs';
import {filter, switchMap, takeUntil} from 'rxjs/operators';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {AuthService} from '@service/auth/auth.service';
import {ModalService} from '@service/modal/modal.service';
import {PreferenceService} from '@service/crm/preference.service';
import {CommonService} from '@service/common/common.service';
import {MessageTemplateComponent} from '../message-template/message-template.component';
import {AskWithMessageComponent} from '../../../components/ask-with-message/ask-with-message.component';


@Component({
    selector: 'app-crm-preferences',
    templateUrl: './crm-preferences.component.html',
    styleUrls: ['./crm-preferences.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CrmPreferencesComponent implements OnInit {
  matTabIndex = 0;
  @ViewChild(TreeComponent) tree: TreeComponent | undefined;
  @ViewChild('signatureModal', {static: true}) signatureModal!: TemplateRef<any>;
  @ViewChild('createNewMessageTemplate', {static: true}) createNewMessageTemplate!: TemplateRef<any>;
  private destroy$ = new Subject<void>();
  private messageTemplateDialogRef!: MatDialogRef<any>;
  public messageTemplates = undefined;
  public userSignature?: SafeHtml;
  public messageTemplateActions: {
    icon: string,
    func: (row: any) => any
  }[];
  widgetOptions = {};

  public editorConfig: any;


  constructor(
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private authService: AuthService,
    private preferenceService: PreferenceService,
    private common: CommonService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private modalService: ModalService,
    private dashboardService: DashboardCrmService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer
  ) {
    this.dashboardService.title = 'CRM - Preferences';
    this.activateTabViaPathParam();
    this.userSignature = this.sanitizer.bypassSecurityTrustHtml(this.authService.currentSignature || '');
    this.editorConfig = {
      toolbar: [
        ['bold', 'italic', 'underline'],
        ['link', 'image'],
        [{size: ['small', false, 'large', 'huge']}],
        [{color: []}, {background: []}],
        ['link'],
        ['clean']
      ]
    };
    this.messageTemplateActions = [
      {
        icon: 'fa-trash',
        func: (id: number) => this.deleteRow(id)
      }
    ];
  }

  ngOnInit(): void {
    if (this.matTabIndex === 0) {
      this.updateTemplateData();
    }
  }

  public updateTemplateData(): void {
    this.preferenceService.getMessageTemplates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((messageTemplates) => {
        if (messageTemplates?.error) {
          this.common.showErrorToast(messageTemplates?.error);
        } else {
          this.messageTemplates = messageTemplates;
          this.cd.markForCheck();
        }
      });
  }

  private activateTabViaPathParam(): void {
    this.activatedRoute.data
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.matTabIndex = data.matTabIndex;
      });
  }

  updateUrl(index: number): void {
    const links = ['mail-templates', 'signature'].map((x) => '/crm/preferences/' + x);
    this.router.navigateByUrl(links[index]);
  }

  public openEditSignature(): void {
    if (this.authService.userId) {
      const editSignatureDialogRef = this.modalService.openEditSignatureModal(+this.authService.userId);
      editSignatureDialogRef.afterClosed().subscribe(result => {
        window.location.reload();
      });
    }
  }

  openMessageTemplate(row: any): void {
    if (row.id) {
      const template = this.dialog.open(MessageTemplateComponent, {
        width: '90%',
        minHeight: '50%',
        maxHeight: '90%',
        panelClass: 'p-0',
        id: 'messageTemplate',
      });
      template.componentInstance.mTmplId = row.id;
      template.afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result?.modified) {
            this.updateTemplateData();
            this.cd.markForCheck();
          }
        });
    }
  }

  openCreateMessageTemplateModal(): void {
    const createTemplate = this.dialog.open(MessageTemplateComponent, {
      id: 'messageTemplate',
      width: '45vw',
      maxHeight: '90vh'
    });
    createTemplate.componentInstance.editMode = false;
    createTemplate.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.modified) {
          this.updateTemplateData();
          this.cd.markForCheck();
        }
      });
  }

  deleteRow(id: number): void {
    if (id) {
      const askMessage = AskWithMessageComponent.openDialog('Remove', 'Confirm remove?', this.dialog);
      askMessage.confirm
        .pipe(
          filter((confirm) => confirm),
          switchMap(() =>
            this.preferenceService.deleteMessageTemplate(id)
          ),
          takeUntil(this.destroy$))
        .subscribe(result => {
          this.updateTemplateData();
          this.cd.markForCheck();
        });
    }
  }

  openRowDetail(row: any): void {
    if (this.matTabIndex === 0
    ) {
      if (row) {
        this.openMessageTemplate(row);
      } else {
        this.openCreateMessageTemplateModal();
      }
    }
  }


  closeDialog(): void {
    this.messageTemplateDialogRef.close();
  }
}
